/** @jsxImportSource @emotion/react */
import type { ApiScanVideoProgress } from '@kaiizen/kai-pro-api';

import { CheckIcon, CloseIcon, InfoCircleIcon, VideoIcon } from '@kaiizen/kai-shiki/react';

import {
  getRowProgressBarPct,
  getSelectedVideoFileSubtitle,
  isRowRemoveDisabled,
  shouldShowRowProgressBar,
} from '@kaiizen/kai-pro-api';
import type { PickerSelectedVideoFile } from './aiScanVideoPickerTypes';
import {
  BAR_TRACK_STYLE,
  barFillStyle,
  FILE_INFO_STYLE,
  FILE_LIST_STYLE,
  FILE_NAME_STYLE,
  FILE_REMOVE_STYLE,
  FILE_ROW_TOP_STYLE,
  fileListScrollStyle,
  fileRowStyle,
  fileSubStyle,
  fileThumbStyle,
  SUCCESS,
} from './aiScanVideoUploadModalStyles';

type AIScanVideoUploadFileListProps = {
  files: readonly PickerSelectedVideoFile[];
  videoProgressById: ReadonlyMap<string, ApiScanVideoProgress>;
  onRemove: (id: string) => void;
};

export const AIScanVideoUploadFileList = ({
  files,
  videoProgressById,
  onRemove,
}: AIScanVideoUploadFileListProps) => {
  if (!files.length) return null;

  return (
    <div
      css={[FILE_LIST_STYLE, fileListScrollStyle(files.length)]}
      data-testid={'ai-scan-video-file-list'}
    >
      {files.map(row => {
        const isBad = row.state === 'bad' || row.state === 'analysisFailed';
        const jobProgress = row.videoId ? videoProgressById.get(row.videoId) : undefined;
        const barPct = getRowProgressBarPct(row, jobProgress);
        const showBar = shouldShowRowProgressBar(row);
        const subtitle = getSelectedVideoFileSubtitle(row, jobProgress);

        return (
          <div key={row.id} css={fileRowStyle(isBad)} data-testid={`ai-scan-video-file-row-${row.id}`}>
            <div css={FILE_ROW_TOP_STYLE}>
              <div css={fileThumbStyle(isBad)}>
                {isBad
                  ? <InfoCircleIcon size={19} color={'currentColor'} aria-hidden={'true'} />
                  : row.stepFlash
                  ? <CheckIcon size={17} color={'currentColor'} aria-hidden={'true'} />
                  : <VideoIcon size={19} color={'currentColor'} aria-hidden={'true'} />}
              </div>
              <div css={FILE_INFO_STYLE}>
                <div css={FILE_NAME_STYLE}>{row.file.name}</div>
                <div css={fileSubStyle(isBad)}>{subtitle}</div>
              </div>
              <button
                type={'button'}
                css={FILE_REMOVE_STYLE}
                disabled={isRowRemoveDisabled(row)}
                aria-label={`Remove ${row.file.name}`}
                onClick={() => onRemove(row.id)}
              >
                <CloseIcon size={15} color={'currentColor'} aria-hidden={'true'} />
              </button>
            </div>
            {showBar && (
              <div css={BAR_TRACK_STYLE}>
                <div css={barFillStyle(barPct, SUCCESS)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
